import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Switch,
  FormControlLabel,
  Button,
  Divider,
  Chip,
  TextField,
  Slider,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import PrivacyConsentService, { PrivacySettings, ConsentData } from '../../services/PrivacyConsentService';

const PrivacySettingsPage: React.FC = () => {
  const [settings, setSettings] = useState<PrivacySettings | null>(null);
  const [consent, setConsent] = useState<ConsentData | null>(null);
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [newBlockedCategory, setNewBlockedCategory] = useState('');

  const privacyService = PrivacyConsentService;
  const userLocation = privacyService.getUserLocation();
  const compliance = privacyService.getComplianceRequirements();

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = () => {
    setSettings(privacyService.getPrivacySettings());
    setConsent(privacyService.getConsent());
  };

  const handleSettingChange = (key: keyof PrivacySettings) => (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!settings) return;
    
    const newSettings = {
      ...settings,
      [key]: event.target.checked
    };
    
    setSettings(newSettings);
    privacyService.setPrivacySettings(newSettings);
  };

  const handleFrequencyCapChange = (event: Event, newValue: number | number[]) => {
    if (!settings) return;
    
    const newSettings = {
      ...settings,
      adFrequencyCap: newValue as number
    };
    
    setSettings(newSettings);
    privacyService.setPrivacySettings(newSettings);
  };

  const handleAddBlockedCategory = () => {
    if (!settings || !newBlockedCategory.trim()) return;
    
    const newSettings = {
      ...settings,
      blockedCategories: [...(settings.blockedCategories || []), newBlockedCategory.trim()]
    };
    
    setSettings(newSettings);
    privacyService.setPrivacySettings(newSettings);
    setNewBlockedCategory('');
  };

  const handleRemoveBlockedCategory = (category: string) => {
    if (!settings) return;
    
    const newSettings = {
      ...settings,
      blockedCategories: (settings.blockedCategories || []).filter(c => c !== category)
    };
    
    setSettings(newSettings);
    privacyService.setPrivacySettings(newSettings);
  };

  const handleConsentChange = (type: keyof ConsentData) => (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!consent || type === 'timestamp' || type === 'version') return;
    
    const newConsent = {
      ...consent,
      [type]: event.target.checked
    };
    
    privacyService.setConsent(newConsent);
    setConsent(privacyService.getConsent());
    loadSettings(); // Reload settings as they may be affected by consent changes
  };

  const handleExportData = () => {
    const userData = privacyService.exportUserData();
    const dataStr = JSON.stringify(userData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    
    const link = document.createElement('a');
    link.href = URL.createObjectURL(dataBlob);
    link.download = `fashion-app-privacy-data-${new Date().toISOString().split('T')[0]}.json`;
    link.click();
    
    setShowExportDialog(false);
  };

  const handleDeleteAllData = () => {
    privacyService.clearAllData();
    setSettings(null);
    setConsent(null);
    setShowDeleteDialog(false);
    
    // Reload the page to reset the app state
    window.location.reload();
  };

  const handleRefreshConsent = () => {
    // This would trigger the consent banner to show again
    privacyService.clearAllData();
    window.location.reload();
  };

  if (!settings || !consent) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">
          Privacy settings are not available. Please accept cookies first.
        </Alert>
        <Button onClick={handleRefreshConsent} startIcon={<RefreshIcon />} sx={{ mt: 2 }}>
          Update Consent Preferences
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
      <Typography variant="h4" component="h1" sx={{ mb: 3, fontWeight: 600 }}>
        Privacy & Ad Settings
      </Typography>

      {/* User Location & Compliance Info */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Your Privacy Rights
        </Typography>
        
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Location: {userLocation?.country || 'Unknown'}
          </Typography>
          
          {compliance.requiresGDPR && (
            <Alert severity="info" sx={{ mt: 1 }}>
              As an EU resident, you have rights under GDPR including data portability and the right to be forgotten.
            </Alert>
          )}
          
          {compliance.requiresCCPA && (
            <Alert severity="info" sx={{ mt: 1 }}>
              As a California resident, you have rights under CCPA including the right to opt-out of data sale.
            </Alert>
          )}
        </Box>
      </Paper>

      {/* Consent Management */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Cookie Consent
        </Typography>
        
        <FormControlLabel
          control={
            <Switch
              checked={consent.functional}
              disabled={true}
              color="primary"
            />
          }
          label={
            <Box>
              <Typography variant="body2">Essential Cookies</Typography>
              <Typography variant="caption" color="text.secondary">
                Required for basic functionality
              </Typography>
            </Box>
          }
          sx={{ mb: 2, display: 'block' }}
        />

        <FormControlLabel
          control={
            <Switch
              checked={consent.analytics}
              onChange={handleConsentChange('analytics')}
              color="primary"
            />
          }
          label={
            <Box>
              <Typography variant="body2">Analytics Cookies</Typography>
              <Typography variant="caption" color="text.secondary">
                Help improve our service
              </Typography>
            </Box>
          }
          sx={{ mb: 2, display: 'block' }}
        />

        <FormControlLabel
          control={
            <Switch
              checked={consent.advertising}
              onChange={handleConsentChange('advertising')}
              color="primary"
            />
          }
          label={
            <Box>
              <Typography variant="body2">Advertising Cookies</Typography>
              <Typography variant="caption" color="text.secondary">
                Show relevant ads
              </Typography>
            </Box>
          }
          sx={{ mb: 2, display: 'block' }}
        />

        <Typography variant="caption" color="text.secondary">
          Last updated: {consent.timestamp ? new Date(consent.timestamp).toLocaleDateString() : 'Never'}
        </Typography>
      </Paper>

      {/* Ad Preferences */}
      {consent.advertising && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Advertisement Preferences
          </Typography>

          <FormControlLabel
            control={
              <Switch
                checked={settings.showPersonalizedAds}
                onChange={handleSettingChange('showPersonalizedAds')}
                color="primary"
              />
            }
            label="Show personalized ads based on my interests"
            sx={{ mb: 2, display: 'block' }}
          />

          <FormControlLabel
            control={
              <Switch
                checked={settings.allowLocationTracking}
                onChange={handleSettingChange('allowLocationTracking')}
                color="primary"
              />
            }
            label="Allow location-based ad targeting"
            sx={{ mb: 3, display: 'block' }}
          />

          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            Ad Frequency Limit: {settings.frequencyCap} ads per page
          </Typography>
          
          <Slider
            value={settings.frequencyCap}
            onChange={handleFrequencyCapChange}
            min={1}
            max={10}
            marks
            valueLabelDisplay="auto"
            sx={{ mb: 3 }}
          />

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            Blocked Ad Categories
          </Typography>

          <Box sx={{ mb: 2 }}>
            {(settings.blockedCategories || []).map((category) => (
              <Chip
                key={category}
                label={category}
                onDelete={() => handleRemoveBlockedCategory(category)}
                sx={{ mr: 1, mb: 1 }}
              />
            ))}
          </Box>

          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <TextField
              size="small"
              placeholder="Add category to block (e.g., 'beauty', 'travel')"
              value={newBlockedCategory}
              onChange={(e) => setNewBlockedCategory(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleAddBlockedCategory()}
              sx={{ flexGrow: 1 }}
            />
            <Button onClick={handleAddBlockedCategory} variant="outlined">
              Add
            </Button>
          </Box>
        </Paper>
      )}

      {/* Data Management */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Data Management
        </Typography>

        <FormControlLabel
          control={
            <Switch
              checked={settings.allowDataCollection}
              onChange={handleSettingChange('allowDataCollection')}
              color="primary"
            />
          }
          label="Allow data collection for service improvement"
          sx={{ mb: 2, display: 'block' }}
        />

        <FormControlLabel
          control={
            <Switch
              checked={settings.allowCookies}
              onChange={handleSettingChange('allowCookies')}
              color="primary"
            />
          }
          label="Allow non-essential cookies"
          sx={{ mb: 3, display: 'block' }}
        />

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            startIcon={<DownloadIcon />}
            onClick={() => setShowExportDialog(true)}
            variant="outlined"
          >
            Export My Data
          </Button>

          <Button
            startIcon={<RefreshIcon />}
            onClick={handleRefreshConsent}
            variant="outlined"
          >
            Update Consent
          </Button>

          <Button
            startIcon={<DeleteIcon />}
            onClick={() => setShowDeleteDialog(true)}
            variant="outlined"
            color="error"
          >
            Delete All Data
          </Button>
        </Box>
      </Paper>

      {/* Export Data Dialog */}
      <Dialog open={showExportDialog} onClose={() => setShowExportDialog(false)}>
        <DialogTitle>Export Your Data</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This will download a JSON file containing all your privacy settings and consent data.
            {compliance.requiresGDPR && ' This is your right under GDPR Article 20 (Data Portability).'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowExportDialog(false)}>Cancel</Button>
          <Button onClick={handleExportData} variant="contained">
            Download Data
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Data Dialog */}
      <Dialog open={showDeleteDialog} onClose={() => setShowDeleteDialog(false)}>
        <DialogTitle>Delete All Privacy Data</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This will permanently delete all your privacy settings and consent data. 
            You will need to provide consent again to use the service.
          </Alert>
          <Typography variant="body2">
            {compliance.requiresGDPR && 'This is your right under GDPR Article 17 (Right to Erasure).'}
            {compliance.requiresCCPA && 'This exercises your right to delete personal information under CCPA.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteDialog(false)}>Cancel</Button>
          <Button onClick={handleDeleteAllData} variant="contained" color="error">
            Delete All Data
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PrivacySettingsPage;